import React, { useState } from 'react';
import { StarIcon, XIcon } from 'lucide-react';
import { AuthorSignature } from '../components/AuthorSignature';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
export function TheMapTheyHidPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-[#f8f7f4] font-sans text-[#1e293b] selection:bg-[#b91c1c] selection:text-white" data-id="element-2016">
      {/* 1. Top Nav Bar */}
      <header className="bg-[#002868] text-white w-full" data-id="element-2017">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between" data-id="element-2018">
          <div className="flex items-center gap-2 font-bold text-lg tracking-tight" data-id="element-2019">
            <StarIcon className="w-5 h-5 fill-current text-[#b91c1c]" data-id="element-2020" />
            <span data-id="element-2021">PatriotsInformer</span>
          </div>
        </div>
      </header>

      {/* 2. Breadcrumb Bar */}
      <div className="bg-white border-b border-gray-200 w-full" data-id="element-2022"></div>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12" style={{
      paddingTop: '2px'
    }} data-id="element-2023">
        {/* 3. Article Section */}
        <article className="space-y-6" data-id="element-2024">
          {/* Title & Meta */}
          <div className="space-y-2" data-id="element-2025">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight" data-id="element-2026">
              The Map They Don't Want You to See: America's Aquifer Drawdown,
              Public and Ignored
            </h1>
          </div>

          {/* Author Bio */}
          <div className="flex flex-row items-center gap-4 sm:gap-6" data-id="element-2027">
            <img src="https://cdn.magicpatterns.com/uploads/qCiNdfGtT6ARUfiGnS7VJm/jack-turner-avatar-2.jpg" alt="Jack Turner" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0" data-id="element-2028" />
            <div className="text-left space-y-1" data-id="element-2029">
              <h3 className="font-bold text-[#0f172a] text-lg" data-id="element-2030">Jack Turner</h3>
              <p className="text-gray-500 text-sm" data-id="element-2031">
                Head of research at PatriotsInformer.com
              </p>
              <p className="text-gray-400 text-xs uppercase tracking-wide pt-1" data-id="element-2032">
                May 5, 2026 · 4 min read
              </p>
            </div>
          </div>

          {/* Article Body */}
          <div className="space-y-6 text-lg text-[#334155] leading-relaxed" data-id="element-2033">
            <p data-id="element-2034">There's a federal report most Americans have never read.</p>
            <p data-id="element-2035">
              It is not classified. It is not buried in some intelligence vault.
              It is published every year, free, by the United States Geological
              Survey, available to anyone with the patience to navigate a
              federal website.
            </p>
            <p data-id="element-2036">
              It is the data on America's groundwater. And it tells a story that
              does not show up on the evening news, does not get mentioned in
              your municipal water bill, and does not make it into the kind of
              conversation most families have at the dinner table — even the
              families that depend, directly or indirectly, on what the report
              describes.
            </p>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-2037">
              The straws are in the same cup
            </h2>

            <p data-id="element-2038">
              Most Americans, when they think of water, think of rivers,
              reservoirs, and rain — surface water, the kind you can photograph
              from a helicopter when Lake Mead drops another twenty feet.
            </p>
            <p data-id="element-2039">
              But the majority of the freshwater you actually use comes from{' '}
              <b data-id="element-2040">groundwater</b>. Aquifers. Underground reservoirs accumulated
              over centuries to millennia, drawn from by wells and pumps.
            </p>
            <p data-id="element-2041">
              The USGS publishes detailed studies of these systems on a
              multi-year cadence. The pattern in nearly all of them is the same:{' '}
              <b data-id="element-2042">withdrawals exceed recharge</b>. The water is being pumped out
              faster than rain and snowmelt are putting it back. Across the
              Ogallala (eight states, feeds the breadbasket), the Central Valley
              (so much groundwater removed that the ground itself has subsided
              over twenty-five feet in places), the Mississippi Alluvial, the
              Edwards — the long-term trend is downward.
            </p>
            <p data-id="element-2043">
              And here's what doesn't make the local news either: most municipal
              water utilities in regions overlying these aquifers are not
              pulling from a magical separate supply. They are drawing from the
              same aquifer system that the private well in your back field is
              drawing from.
            </p>

            <blockquote className="border-l-4 border-[#b91c1c] pl-6 py-2 italic text-[#0f172a]" data-id="element-2044">
              The straws are different lengths, but they're in the same cup.
              When the aquifer drops, every household in that region is on the
              same timeline — just at different stations.
            </blockquote>

            <p data-id="element-2045">
              The map is not hidden. It's just been ignored long enough that
              finding it feels like discovery. Find it. Read it. Decide what
              your household does about it — before the rationing notice, not
              after.
            </p>
            <p className="font-bold text-[#0f172a]" data-id="element-2046">Learn. Build. Be ready.</p>
          </div>
        </article>

        {/* 4. 'You Might Also Like' Section */}
        <section className="space-y-8 pt-8 border-t border-gray-200" data-id="element-2047">
          <h2 className="text-3xl font-bold text-[#0f172a]" data-id="element-2048">
            You Might Also Like:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8" data-id="element-2049">
            {/* Card 1 */}
            <div className="group cursor-pointer space-y-3" data-id="element-2050">
              <a href="https://trk.adsdomredir.com/click/2" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-2051">
                <img src="https://cdn.magicpatterns.com/uploads/tfBxRA9s5qgvhpKFp12h5o/unnamed_(7).jpg" alt="The Lost SuperFoods book" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2052" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-2053">
                <a href="https://trk.adsdomredir.com/click/2" data-id="element-2054">
                  Superfoods That You Can Store Without Refrigeration for Years
                </a>
              </h3>
            </div>

            {/* Card 2 */}
            <div className="group cursor-pointer space-y-3" data-id="element-2055">
              <a href="https://trk.adsdomredir.com/click/3" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-2056">
                <img src="https://cdn.magicpatterns.com/uploads/aeW4oPiWoeR5Wzq6FuUxBZ/new5notext.png" alt="Vegetable garden" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2057" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-2058">
                <a href="https://trk.adsdomredir.com/click/3" data-id="element-2059">
                  The Underground Energy Method They Don't Want You To Know
                  About
                </a>
              </h3>
            </div>

            {/* Card 3 */}
            <div className="group cursor-pointer space-y-3" data-id="element-2060">
              <a href="https://trk.adsdomredir.com/click/4" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-2061">
                <img src="https://cdn.magicpatterns.com/uploads/7ksskNxA12GTBLDP2Kydn9/img18.png" alt="Man holding tools" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2062" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-2063">
                <a href="https://trk.adsdomredir.com/click/4" data-id="element-2064">
                  Nature-Inspired 3D Solar Sphere Generates Energy On Demand
                  (Watch Video)
                </a>
              </h3>
            </div>
          </div>

          <AuthorSignature data-id="element-2065" />
        </section>
      </main>

      {/* 5. Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-12" data-id="element-2066">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6" data-id="element-2067">
          <div className="flex items-center justify-center gap-2 text-[#b91c1c]" data-id="element-2068">
            <div className="relative w-10 h-10" data-id="element-2069">
              <StarIcon className="w-10 h-10 fill-current text-[#002868]" data-id="element-2070" />
              <StarIcon className="w-4 h-4 fill-current text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-2071" />
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-4 max-w-2xl mx-auto leading-relaxed" data-id="element-2072">
            <p data-id="element-2073">
              © 2018-2026 PatriotsInformer Media, LLC | All Rights Reserved |
              63 N. Burritt - Buffalo, WY 82834 United States
            </p>
            <p className="space-x-2" data-id="element-2074">
              <button onClick={() => setActiveModal('terms')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-2075">
                Terms & Conditions
              </button>
              <span data-id="element-2076">|</span>
              <button onClick={() => setActiveModal('privacy')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-2077">
                Privacy Policy
              </button>
              <span data-id="element-2078">|</span>
              <button onClick={() => setActiveModal('contact')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-2079">
                Contact Us
              </button>
            </p>
            <p data-id="element-2080">
              No part of this report may be reproduced or placed on any
              electronic medium without written permission from the publisher.
              Information contained herein is obtained from sources believed to
              be reliable, but its accuracy cannot be guaranteed.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-2081">
          <div className="absolute inset-0 bg-black/50" data-id="element-2082" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-2083">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-2084">
              <h2 className="text-lg font-bold text-[#0f172a]" data-id="element-2085">
                {activeModal === 'terms' && 'Terms & Conditions'}
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'contact' && 'Contact Us'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-2086">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-2087" />
              </button>
            </div>

            <div className="px-6 py-6 text-sm text-[#334155] leading-relaxed space-y-4" data-id="element-2088">
              {activeModal === 'terms' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-2089">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-2090">
                    Welcome to PatriotsInformer.com. By accessing or using our
                    website, you agree to be bound by these Terms & Conditions.
                  </p>
                </>}
              {activeModal === 'privacy' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-2091">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-2092">
                    PatriotsInformer Media, LLC is committed to protecting your
                    privacy.
                  </p>
                </>}
              {activeModal === 'contact' && <>
                  <p data-id="element-2093">
                    We'd love to hear from you. Feel free to reach out using the
                    information below:
                  </p>
                  <div className="bg-[#f8f7f4] rounded-lg p-5 space-y-3 border border-gray-200" data-id="element-2094">
                    <h3 className="font-bold text-[#0f172a]" data-id="element-2095">
                      PatriotsInformer Media, LLC
                    </h3>
                    <p data-id="element-2096">
                      <span className="font-semibold text-[#0f172a]" data-id="element-2097">
                        Email:
                      </span>{' '}
                      <a href="mailto:support@patriotsinformer.com" className="text-[#002868] underline" data-id="element-2098">
                        support@patriotsinformer.com
                      </a>
                    </p>
                  </div>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}